import { useEffect, useState, useSyncExternalStore } from 'react';
import { todayIn } from '@autoday/core';
import { getStore } from '../store/appStore.ts';

export function useMediaQuery(q: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(q);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => window.matchMedia(q).matches,
    () => false,
  );
}

export const useIsDesktop = () => useMediaQuery('(min-width: 1024px)');
export const useIsMobile = () => !useMediaQuery('(min-width: 768px)');

/** Dagens datum i användarens zon, uppdateras varje minut (injicerbar klocka respekteras). */
export function useToday(): string {
  const store = getStore();
  const tz = store.state.settings.timeZone;
  const [d, setD] = useState(() => todayIn(tz, store.clock.now()).toString());
  useEffect(() => {
    const t = setInterval(() => setD(todayIn(store.state.settings.timeZone, store.clock.now()).toString()), 60_000);
    setD(todayIn(tz, store.clock.now()).toString());
    return () => clearInterval(t);
  }, [tz, store]);
  return d;
}

export type Route =
  | { name: 'idag' }
  | { name: 'utrymme' }
  | { name: 'plus' }
  | { name: 'kalender' }
  | { name: 'viktigt' }
  | { name: 'kalendrar' }
  | { name: 'import' }
  | { name: 'installningar' }
  | { name: 'notiser' }
  | { name: 'aterstall' }
  | { name: 'post'; ref: string };

export function parseRoute(hash: string): Route {
  const h = hash.replace(/^#\/?/, '').split('?')[0]!;
  const [a, b] = h.split('/');
  switch (a) {
    case 'plus':
    case 'utrymme':
    case 'kalender':
    case 'viktigt':
    case 'kalendrar':
    case 'import':
    case 'installningar':
    case 'notiser':
      return { name: a };
    case 'aterstall-losenord':
      return { name: 'aterstall' };
    case 'post':
      return b ? { name: 'post', ref: decodeURIComponent(b) } : { name: 'idag' };
    default:
      return { name: 'idag' };
  }
}

export function useRoute(): Route {
  const hash = useSyncExternalStore(
    (cb) => {
      window.addEventListener('hashchange', cb);
      return () => window.removeEventListener('hashchange', cb);
    },
    () => window.location.hash,
  );
  return parseRoute(hash);
}

export function navigate(path: string) {
  window.location.hash = path.startsWith('#') ? path : '#/' + path.replace(/^\//, '');
}
